import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { JobSummaryDto } from '@models/job-summary-dto.interface';
import { JobDetailDto } from '@models/job-detail-dto.interface';
import { EditJobDto } from '@models/edit-job-dto.interface';
import { SaveJobDto } from '@models/save-job-dto.interface';
import { GridRow } from '@shared/models/grid-row.interface';
import { PagedResult } from '@shared/models/paged-result.model';
import { SearchParameters } from '@shared/models/search-parameters.model';
import { TestMetrics } from '@models/test-metrics.interface';

export interface JobRequestListQuery {
  projectId: number;
  year: number | null;
  filter: string | null;
}

export interface JobRequestListByLookupQuery {
  lookupKey: string;
  year: number | null;
  filter: string | null;
}

export interface JobRequestListGlobalQuery {
  year: number | null;
  filter: string | null;
}

export interface JobRequestSearchQuery {
  wellId: number | null;
  rigId: number | null;
  businessUnitId: number | null;
  requestType: string | null;
  testTypeIds: number[] | null;
  startDateFrom: string | null;
  startDateTo: string | null;
  densityFrom: number | null;
  densityTo: number | null;
  material: string | null;
}

export interface JobRequestSearchResult {
  jobId: number;
  jobRequestId: number;
  jobName: string;
  requestName: string | null;
  startDate: string | null;
  wellName: string | null;
  rigName: string | null;
  businessUnitName: string | null;
  requestType: string;
  density: number | null;
  material: string | null;
  testTypes: string;
}

export interface TestMetricsQuery {
  fromDate: string;
  toDate: string;
  businessUnitId: number | null;
  assetClassId: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class JobsService {
  private readonly apiService = inject(ApiService);

  searchJobs(year: number | null, jobIds: number[] | null): Observable<JobSummaryDto[]> {
    const params: string[] = [];
    if (year != null) params.push(`year=${year}`);
    if (jobIds?.length) params.push(`jobIds=${jobIds.join(',')}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.apiService.get(`jobs${query}`);
  }

  getJobById(id: number): Observable<JobDetailDto> {
    return this.apiService.get(`jobs/${id}`);
  }

  getJobsByProject(searchParameters: SearchParameters<JobRequestListQuery>): Observable<PagedResult<GridRow>> {
    const request: SearchParameters<JobRequestListQuery> = {
      ...searchParameters,
      query: {
        ...searchParameters.query,
        filter: searchParameters.query.filter?.trim() || null
      }
    };
    return this.apiService.post('jobs/by-project', request);
  }

  getJobsByLookup(searchParameters: SearchParameters<JobRequestListByLookupQuery>): Observable<PagedResult<GridRow>> {
    const request: SearchParameters<JobRequestListByLookupQuery> = {
      ...searchParameters,
      query: {
        ...searchParameters.query,
        filter: searchParameters.query.filter?.trim() || null
      }
    };
    return this.apiService.post('jobs/by-lookup', request);
  }

  getJobsGlobal(searchParameters: SearchParameters<JobRequestListGlobalQuery>): Observable<PagedResult<GridRow>> {
    const request: SearchParameters<JobRequestListGlobalQuery> = {
      ...searchParameters,
      query: {
        ...searchParameters.query,
        filter: searchParameters.query.filter?.trim() || null
      }
    };
    return this.apiService.post('jobs/global', request);
  }

  searchJobRequests(searchParameters: SearchParameters<JobRequestSearchQuery>): Observable<PagedResult<JobRequestSearchResult>> {
    return this.apiService.post('jobs/request-search', searchParameters);
  }

  getJobForEdit(id: number): Observable<EditJobDto> {
    return this.apiService.get(`jobs/${id}/edit`);
  }

  getNextJobName(): Observable<{ name: string }> {
    return this.apiService.get('jobs/next-name');
  }

  createJob(dto: SaveJobDto): Observable<number> {
    return this.apiService.post('jobs', dto);
  }

  updateJob(id: number, dto: SaveJobDto): Observable<void> {
    return this.apiService.put(`jobs/${id}`, dto);
  }

  getTestTypesForMudCheck(): Observable<any[]> {
    return this.apiService.get('jobs/mudcheck/test-types');
  }

  getTestMetrics(query: TestMetricsQuery): Observable<TestMetrics> {
    const params = new URLSearchParams({
      fromDate: query.fromDate,
      toDate: query.toDate,
    });
    if (query.businessUnitId != null) params.set('businessUnitId', String(query.businessUnitId));
    if (query.assetClassId != null) params.set('assetClassId', String(query.assetClassId));

    return this.apiService.get(`jobs/metrics?${params.toString()}`);
  }
}
